import { SearchIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import useSWR, { mutate as globalMutate } from 'swr'

import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceDiv,
  PreferenceInfo,
  PreferenceInput,
  PreferenceScreen,
  PreferenceSubmit,
} from '../../components/Preference'
import { probeCogoExtensionFast } from '../../lib/cogoExtension'
import { searchMoreCogoArticles } from '../../lib/cogoEnrichWorker'
import { rankCandidateOptions } from '../../lib/cogoPrefs'
import { formatCogoChoicePrice } from '../../lib/cogoPrice'
import {
  choicePackUnitNotice,
  choiceUnitTypeButtons,
  choiceUnitTypes,
  unitTypeLabel,
  unitTypePrimaryBtnClass,
  unitTypeSecondaryBtnClass,
  type UnitType,
} from '../../lib/cogoUnits'
import { fetcher } from '../../lib/swr'
import {
  articleMetaLine,
  candidateToMappedDraft,
  type CogoCandidateArticle,
  type CogoMappedArticleDraft,
} from './cogoProductMap'
import type { CogoUnmappedProductListItem } from './CogoProductsSettings'

type DetailResponse = {
  product: { id: number; display: string; unit: string } | null
  map: { productId: number; articles: CogoMappedArticleDraft[] } | null
  candidates: {
    status: 'pending' | 'ready' | 'empty' | 'error'
    articles: CogoCandidateArticle[]
    lastEnrichedAt: string | null
  } | null
}

type DraftEntry = {
  technicalProductId: string
  priority: number
  unitType: UnitType | null
  display: string
  article: CogoMappedArticleDraft
}

function unitTypeOptions(modes: UnitType[]) {
  return [
    { value: '' as const, label: 'Auto' },
    ...modes.map((m) => ({
      value: m,
      label: unitTypeLabel(m),
    })),
  ]
}

function toDraft(articles: CogoMappedArticleDraft[]): DraftEntry[] {
  return articles.map((a) => ({
    technicalProductId: a.technicalProductId,
    priority: a.priority >= 1 ? a.priority : 1,
    unitType: a.unitType,
    display: a.display,
    article: a,
  }))
}

function draftsEqual(a: DraftEntry[], b: DraftEntry[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!
    const y = b[i]!
    if (
      x.technicalProductId !== y.technicalProductId ||
      x.priority !== y.priority ||
      x.unitType !== y.unitType
    ) {
      return false
    }
  }
  return true
}

export default function CogoProductMapDetailSettings() {
  const { productId: productIdParam } = useParams<{ productId: string }>()
  const productId = Number(productIdParam)
  const navigate = useNavigate()
  const detail = useSWR<DetailResponse>(
    Number.isFinite(productId) && productId > 0
      ? `/api/cogo/map/${productId}`
      : null,
    fetcher
  )
  const maps = useSWR<{ unmapped?: CogoUnmappedProductListItem[] }>(
    '/api/cogo/maps',
    fetcher
  )

  const [draft, setDraft] = useState<DraftEntry[]>([])
  const [baseline, setBaseline] = useState<DraftEntry[]>([])
  const [busy, setBusy] = useState(false)
  const [searchQ, setSearchQ] = useState('')
  const [searching, setSearching] = useState(false)
  const searchInitialized = useRef(false)
  const saveGen = useRef(0)
  const draftRef = useRef(draft)
  const baselineRef = useRef(baseline)
  draftRef.current = draft
  baselineRef.current = baseline

  useEffect(() => {
    if (detail.error) {
      toast.error(detail.error.message || 'Kon map niet laden')
    }
  }, [detail.error])

  useEffect(() => {
    const articles = detail.data?.map?.articles ?? []
    if (!detail.data) return
    const next = toDraft(articles)
    draftRef.current = next
    setDraft(next)
    setBaseline(next)
  }, [detail.data])

  useEffect(() => {
    searchInitialized.current = false
    setSearchQ('')
  }, [productId])

  useEffect(() => {
    if (searchInitialized.current) return
    const display = detail.data?.product?.display?.trim()
    if (!display) return
    searchInitialized.current = true
    setSearchQ(display)
  }, [detail.data?.product?.display])

  const product = detail.data?.product

  const nextUnmappedHref = useMemo(() => {
    const unmapped = maps.data?.unmapped ?? []
    if (unmapped.length === 0) return undefined
    const idx = unmapped.findIndex((p) => p.productId === productId)
    const next = idx >= 0 ? unmapped[idx + 1] : unmapped[0]
    if (!next) return undefined
    return `/settings/cogo/products/${next.productId}`
  }, [maps.data?.unmapped, productId])

  const sortedDraft = useMemo(
    () =>
      [...draft].sort(
        (a, b) => a.priority - b.priority || a.display.localeCompare(b.display)
      ),
    [draft]
  )

  const mappedTpc = useMemo(
    () => new Set(draft.map((d) => d.technicalProductId)),
    [draft]
  )

  const candidateArticles = useMemo(() => {
    const raw = detail.data?.candidates?.articles ?? []
    return rankCandidateOptions(raw).filter(
      (a) => !mappedTpc.has(a.technicalProductId)
    )
  }, [detail.data?.candidates?.articles, mappedTpc])

  const candStatus = detail.data?.candidates?.status ?? null

  async function persist(next: DraftEntry[]) {
    if (draftsEqual(next, baselineRef.current)) return
    const gen = ++saveGen.current
    setBusy(true)
    try {
      if (next.length === 0) {
        const res = await fetch(`/api/cogo/map/${productId}`, {
          method: 'DELETE',
          credentials: 'include',
        })
        if (gen !== saveGen.current) return
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          toast.error(data.message || 'Verwijderen mislukt')
          await detail.mutate()
          return
        }
        toast.success('Koppeling verwijderd')
        void globalMutate('/api/cogo/maps')
        return
      }

      const res = await fetch(`/api/cogo/map/${productId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entries: next.map((d) => ({
            technicalProductId: d.technicalProductId,
            unitType: d.unitType,
            priority: d.priority,
          })),
        }),
      })
      if (gen !== saveGen.current) return
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.message || 'Bewaren mislukt')
        await detail.mutate()
        return
      }
      setBaseline(next)
      void globalMutate('/api/cogo/maps')
    } finally {
      if (gen === saveGen.current) setBusy(false)
    }
  }

  function updateEntry(
    technicalProductId: string,
    patch: Partial<Pick<DraftEntry, 'priority' | 'unitType'>>
  ) {
    const next = draftRef.current.map((d) =>
      d.technicalProductId === technicalProductId ? { ...d, ...patch } : d
    )
    draftRef.current = next
    setDraft(next)
    void persist(next)
  }

  function removeEntry(technicalProductId: string) {
    const next = draftRef.current.filter(
      (d) => d.technicalProductId !== technicalProductId
    )
    draftRef.current = next
    setDraft(next)
    void persist(next)
  }

  function addCandidate(
    opt: CogoCandidateArticle,
    unitType: UnitType | null = null
  ) {
    const url = opt.assortmentUrl?.trim() || ''
    if (!url || !/\/assortiment\//i.test(url) || /rootCategoryId=/i.test(url)) {
      toast.error('Geen productpagina voor dit artikel', {
        description:
          'Zoek opnieuw via Collect&Go zodat de link bekend is, en kies opnieuw.',
      })
      return
    }
    if (
      draftRef.current.some(
        (d) => d.technicalProductId === opt.technicalProductId
      )
    )
      return
    const article = candidateToMappedDraft(opt, { priority: 1, unitType })
    const next = [
      ...draftRef.current,
      {
        technicalProductId: article.technicalProductId,
        priority: article.priority,
        unitType: article.unitType,
        display: article.display,
        article,
      },
    ]
    draftRef.current = next
    setDraft(next)
    void persist(next)
  }

  async function searchAlternatives() {
    const q = searchQ.trim()
    if (!q) {
      toast.error('Geef een zoekterm in')
      return
    }
    const status = await probeCogoExtensionFast()
    if (!status.installed) {
      toast.message('Chrome-extensie nodig', {
        description:
          'Installeer de Collect&Go-extensie om artikelen te zoeken.',
      })
      return
    }

    const known = detail.data?.candidates?.articles ?? []
    const excludeTechnicalProductIds = [
      ...new Set([
        ...known.map((a) => a.technicalProductId),
        ...draftRef.current.map((d) => d.technicalProductId),
      ]),
    ].filter(Boolean)
    const excludeUrls = known
      .map((a) => a.assortmentUrl)
      .filter((u): u is string => Boolean(u))

    setSearching(true)
    try {
      const articles = await searchMoreCogoArticles({
        display: q,
        excludeTechnicalProductIds,
        excludeUrls,
        limit: 5,
      })
      if (articles.length === 0) {
        toast.message(
          excludeTechnicalProductIds.length > 0
            ? 'Geen nieuwe artikelen'
            : 'Geen artikelen gevonden',
          {
            description:
              excludeTechnicalProductIds.length > 0
                ? 'Deze zoekterm leverde alleen treffers die je al hebt. Probeer een andere term.'
                : 'Probeer een andere zoekterm op Collect&Go.',
          }
        )
        return
      }
      const res = await fetch(`/api/cogo/map/${productId}/candidates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ articles, excludeTechnicalProductIds }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        message?: string
      }
      if (!res.ok) {
        toast.error(data.message || 'Zoeken mislukt')
        return
      }
      toast.success(
        `${articles.length} artikel${articles.length === 1 ? '' : 'en'} gevonden`
      )
      await detail.mutate()
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      if (msg === 'needs_login') {
        toast.message('Log in bij Collect&Go', {
          description: 'Daarna opnieuw zoeken.',
        })
      } else {
        toast.error(msg === 'blocked' ? 'Collect&Go geblokkeerd' : msg)
      }
    } finally {
      setSearching(false)
    }
  }

  if (detail.isLoading && !detail.data) {
    return (
      <PreferenceScreen title="Product-map" backHref="/settings/cogo/products">
        <PreferenceInfo title="Laden…" />
      </PreferenceScreen>
    )
  }

  if (!product) {
    return (
      <PreferenceScreen title="Product-map" backHref="/settings/cogo/products">
        <PreferenceCategory filled>
          <PreferenceInfo
            title="Niet gevonden"
            summary="Dit product bestaat niet (meer)."
          />
        </PreferenceCategory>
      </PreferenceScreen>
    )
  }

  return (
    <PreferenceScreen
      title={product.display + (product.unit ? ` (${product.unit})` : '')}
      backHref="/settings/cogo/products"
      nextHref={nextUnmappedHref}
      nextTitle="Volgend te koppelen product"
    >
      {sortedDraft.length > 0 ? (
        <PreferenceCategory
          title={`Voorkeur voor artikel${sortedDraft.length === 1 ? '' : 's'}`}
          filled
        >
          {sortedDraft.map((entry) => {
            const price = formatCogoChoicePrice(entry.article)
            const meta = articleMetaLine(entry.article)
            return (
              <PreferenceDiv
                key={entry.technicalProductId}
                className="flex p-0 bg-white/6 first:rounded-t-2xl last:rounded-b-2xl overflow-hidden"
              >
                {entry.article.hasImage !== false ? (
                  <img
                    src={`/api/cogo/articles/${encodeURIComponent(entry.technicalProductId)}/image`}
                    alt=""
                    className="w-32 self-stretch object-contain bg-white shrink-0"
                    loading="lazy"
                    onError={(e) => {
                      ;(e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                ) : null}
                <div className="min-w-0 flex-1 flex flex-col gap-3 px-5 py-4">
                  <div className="flex gap-3 items-start">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium leading-snug">
                        {entry.display}
                      </p>
                      <p className="text-xs opacity-60 mt-0.5">
                        {meta} &middot;{' '}
                        {entry.article.assortmentUrl ? (
                          <a
                            href={entry.article.assortmentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-lime-300/90 hover:underline mt-1 inline-block"
                          >
                            Open op Collect&Go
                          </a>
                        ) : null}
                      </p>
                    </div>
                    {price ? (
                      <p className="shrink-0 font-medium tabular-nums opacity-80">
                        {price}
                      </p>
                    ) : null}
                  </div>

                  <div className="grid gap-1 -mx-1">
                    {draft.length > 1 ? (
                      <div className="w-full flex items-center justify-between gap-3 pl-5 pr-4 py-3 bg-green-900 first:rounded-t-2xl last:rounded-b-2xl rounded">
                        <div className="text-xl leading-snug shrink-0">
                          Prioriteit
                        </div>
                        <div
                          className="flex flex-wrap justify-end gap-1 rounded-lg bg-black/25 p-1"
                          role="group"
                          aria-label="Prioriteit"
                        >
                          {([1, 2, 3] as const).map((n) => {
                            const selected =
                              Math.min(3, Math.max(1, entry.priority)) === n
                            return (
                              <button
                                key={n}
                                type="button"
                                aria-pressed={selected}
                                onClick={() =>
                                  updateEntry(entry.technicalProductId, {
                                    priority: n,
                                  })
                                }
                                className={`rounded-md px-2.5 py-1.5 text-sm leading-tight tabular-nums transition-colors ${
                                  selected
                                    ? 'bg-green-600 text-white'
                                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                                }`}
                              >
                                {n}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ) : null}
                    {entry.article.unitTypes.length >= 2 ? (
                      <div className="w-full flex items-center justify-between gap-3 pl-5 pr-4 py-3 bg-green-900 first:rounded-t-2xl last:rounded-b-2xl rounded">
                        <div className="text-xl leading-snug shrink-0">
                          Eenheid
                        </div>
                        <div
                          className="flex flex-wrap justify-end gap-1 rounded-lg bg-black/25 p-1"
                          role="group"
                          aria-label="Eenheid"
                        >
                          {unitTypeOptions(entry.article.unitTypes).map(
                            (opt) => {
                              const selected =
                                (entry.unitType ?? '') === opt.value
                              return (
                                <button
                                  key={opt.value || 'auto'}
                                  type="button"
                                  aria-pressed={selected}
                                  onClick={() =>
                                    updateEntry(entry.technicalProductId, {
                                      unitType: (opt.value ||
                                        null) as UnitType | null,
                                    })
                                  }
                                  className={`rounded-md px-2.5 py-1.5 text-sm leading-tight transition-colors ${
                                    selected
                                      ? 'bg-green-600 text-white'
                                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              )
                            }
                          )}
                        </div>
                      </div>
                    ) : null}
                    <PreferenceButton
                      icon={<Trash2Icon className="h-6 w-6" />}
                      title="Vergeten"
                      onPress={() => removeEntry(entry.technicalProductId)}
                      isDestructive
                      disabled={busy}
                      filled
                    />
                  </div>
                </div>
              </PreferenceDiv>
            )
          })}
        </PreferenceCategory>
      ) : null}

      <PreferenceCategory
        title={
          candidateArticles.length > 0
            ? `${candidateArticles.length} ${candidateArticles.length === 1 ? 'kandidaat' : 'kandidaten'}`
            : 'Kandidaten'
        }
        filled
      >
        {candidateArticles.length > 0 ? (
          candidateArticles.map((opt) => {
            const modes = choiceUnitTypes(opt)
            const multi = modes.length > 1
            const buttons = choiceUnitTypeButtons(modes)
            const packNotice = choicePackUnitNotice(modes)
            const price = formatCogoChoicePrice(opt)
            const meta = articleMetaLine(opt)
            const thumb =
              opt.hasImage !== false ? (
                <img
                  src={`/api/cogo/articles/${encodeURIComponent(opt.technicalProductId)}/image`}
                  alt=""
                  className="w-16 self-stretch object-contain bg-white shrink-0"
                  loading="lazy"
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              ) : null
            return (
              <PreferenceDiv
                key={opt.technicalProductId}
                className="flex p-0 bg-white/6 first:rounded-t-2xl last:rounded-b-2xl overflow-hidden"
              >
                {multi ? (
                  <>
                    {thumb}
                    <div className="min-w-0 flex-1 px-5 py-4">
                      <div className="flex gap-3 items-start">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium leading-snug">
                            {opt.display}
                          </p>
                          {meta ? (
                            <p className="text-xs opacity-60 mt-0.5">{meta}</p>
                          ) : null}
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {buttons.map((mode, i) => (
                              <button
                                key={mode}
                                type="button"
                                disabled={busy}
                                className={
                                  i === 0
                                    ? unitTypePrimaryBtnClass
                                    : unitTypeSecondaryBtnClass
                                }
                                onClick={() => addCandidate(opt, mode)}
                              >
                                {unitTypeLabel(mode)}
                              </button>
                            ))}
                          </div>
                          {packNotice ? (
                            <button
                              type="button"
                              disabled={busy}
                              className="mt-1.5 text-xs text-sky-700 hover:underline disabled:opacity-50"
                              onClick={() => addCandidate(opt, 'PA')}
                            >
                              Ook per pak
                            </button>
                          ) : null}
                        </div>
                        {price ? (
                          <p className="shrink-0 font-medium tabular-nums opacity-80">
                            {price}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    className="w-full flex text-left disabled:opacity-50"
                    onClick={() => addCandidate(opt, null)}
                  >
                    {thumb}
                    <span className="min-w-0 flex-1 px-5 py-4 flex gap-3 items-start">
                      <span className="min-w-0 flex-1">
                        <span className="font-medium block leading-snug">
                          {opt.display}
                        </span>
                        {meta ? (
                          <span className="text-xs opacity-60 block mt-0.5">
                            {meta}
                          </span>
                        ) : null}
                      </span>
                      {price ? (
                        <span className="shrink-0 font-medium tabular-nums opacity-80">
                          {price}
                        </span>
                      ) : null}
                    </span>
                  </button>
                )}
              </PreferenceDiv>
            )
          })
        ) : candStatus === 'pending' || !detail.data?.candidates ? (
          <PreferenceInfo
            title="Nog geen kandidaten"
            summary={
              candStatus === 'pending'
                ? 'Verrijking loopt nog — even geduld.'
                : 'Start verrijking via Collect&Go sync, of wacht tot achtergrond-verrijking klaar is.'
            }
          />
        ) : candStatus === 'empty' || candStatus === 'error' ? (
          <PreferenceInfo
            title="Geen treffers"
            summary="Collect&Go vond geen artikelen voor dit product."
          />
        ) : sortedDraft.length > 0 ? null : (
          <PreferenceInfo
            title="Geen kandidaten"
            summary="Er zijn nog geen artikelen om te kiezen."
          />
        )}
      </PreferenceCategory>

      <PreferenceCategory title="Zoeken op Collect&Go" filled>
        <PreferenceInput
          title=""
          value={searchQ}
          onChange={setSearchQ}
          placeholder="Zoekterm…"
          icon={<SearchIcon className="h-6 w-6" />}
          disabled={searching}
        />
        <PreferenceSubmit
          title={searching ? 'Zoeken…' : 'Zoek alternatieven'}
          onPress={() => void searchAlternatives()}
          disabled={searching || busy}
        />
      </PreferenceCategory>
    </PreferenceScreen>
  )
}
